import { PaperClipIcon } from '@heroicons/react/24/outline';
import React, { useState, useCallback, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { useParams } from 'react-router-dom';

import Filename from '../../../components/Filename';
import LoadingSpinner from '../../../components/LoadingSpinner';
import { DatevBookingData, LexOfficeBookingData, BookingImportJob } from '../../../types';
import { useReceiptActions } from '../../BulkUploadPage/hooks/useReceiptActions';
import { useBookingImportJobPolling } from '../hooks/useBookingImportJobPolling';
import { useGenerateBooking } from '../hooks/useGenerateBooking';
import { useReceiptQuery } from '../hooks/useReceiptQuery';
import { useRetryBookingJob } from '../hooks/useRetryBookingJob';
import { Message } from '../types';
import { markdownComponents } from '../utils/markdownComponents';
import { getFormattedTime } from '../utils/timeUtils';

import DatevBookingModal from './DatevBookingModal';
import LexOfficeBookingModal from './LexOfficeBookingModal';
import ReactionStatusBadge from './ReactionStatusBadge';
import ThinkingDots from './ThinkingDots';

interface MessageBubbleProps {
  message: Message;
}

const MessageBubble: React.FC<MessageBubbleProps> = ({ message }) => {
  const { chatId } = useParams<{ chatId?: string }>();
  const isUserMessage = message.sender !== 'Acconto.ai';
  const formattedTime = getFormattedTime(message.timestamp);

  const [showBookingData, setShowBookingData] = useState(false);
  const [showDatevBookingData, setShowDatevBookingData] = useState(false);
  const [importJobId, setImportJobId] = useState<string | null>(null);
  const [generationScheduled, setGenerationScheduled] = useState(false);

  // Only fetch receipt data for assistant messages
  const assistantReceiptId = isUserMessage ? null : message.receiptId;
  const { receipt } = useReceiptQuery(assistantReceiptId);
  const { importReceipt, rejectReceipt, isImporting, isRejecting } =
    useReceiptActions();
  const { retryBookingJob, isRetrying } = useRetryBookingJob(chatId);
  const { generateBooking, isGenerating: isGeneratingBooking } =
    useGenerateBooking(chatId);

  const { job: importJob } = useBookingImportJobPolling(
    importJobId,
    assistantReceiptId ?? null
  );

  const isImportInProgress =
    !!importJob &&
    (importJob.status === 'pending' || importJob.status === 'importing');

  // Local state for edited booking data
  const [updatedBookingData, setUpdatedBookingData] = useState<
    LexOfficeBookingData | undefined
  >(undefined);
  const [updatedDatevData, setUpdatedDatevData] = useState<
    DatevBookingData[] | undefined
  >(undefined);

  // Derive booking data from receipt
  const lexOfficeBookingData = receipt?.lexOfficeBooking ?? undefined;
  const datevBookingData = receipt?.datevBooking ?? undefined;

  // Use local edits if available, otherwise receipt data
  const displayLexData = updatedBookingData ?? lexOfficeBookingData;
  const displayDatevData = updatedDatevData ?? datevBookingData;

  // Clear scheduled flag once receipt status moves past 'ready'
  useEffect(() => {
    if (generationScheduled && receipt?.status && receipt.status !== 'ready') {
      setGenerationScheduled(false);
    }
  }, [generationScheduled, receipt?.status]);

  // Show generation spinner when job is pending but receipt not yet linked
  const isGenerating =
    !isUserMessage && !!message.bookingGenerationJobId && !message.receiptId;
  // Show generation spinner when receipt exists but is still being processed
  const isReceiptProcessing =
    !isUserMessage &&
    receipt &&
    ['pending', 'text-extracting', 'generation-scheduled', 'generating'].includes(receipt.status);
  // Show spinner between HTTP response and backend picking up the job
  const isAwaitingGeneration =
    generationScheduled && !isGeneratingBooking && receipt?.status === 'ready';

  const handleSaveBookingData = useCallback((data: LexOfficeBookingData) => {
    setUpdatedBookingData(data);
  }, []);

  const handleRestoreBookingData = useCallback(() => {
    setUpdatedBookingData(undefined);
  }, []);

  const handleSaveDatevData = useCallback((data: DatevBookingData[]) => {
    setUpdatedDatevData(data);
  }, []);

  const handleRestoreDatevData = useCallback(() => {
    setUpdatedDatevData(undefined);
  }, []);

  const handleImport = useCallback(
    async (
      receiptId: string,
      hasChanges: boolean,
      proposedBookingData?: LexOfficeBookingData
    ): Promise<BookingImportJob | null> => {
      const job = await importReceipt(
        receiptId,
        hasChanges,
        proposedBookingData
      );
      if (job) {
        setImportJobId(job.id);
      }
      return job;
    },
    [importReceipt]
  );

  const handleReject = useCallback(
    async (receiptId: string): Promise<boolean> => {
      return rejectReceipt(receiptId);
    },
    [rejectReceipt]
  );

  return (
    <>
      {/* Modals - only for assistant messages */}
      {!isUserMessage &&
        showBookingData &&
        displayLexData &&
        receipt &&
        message.receiptId && (
          <LexOfficeBookingModal
            receiptId={message.receiptId}
            receipt={receipt}
            bookingData={displayLexData}
            originalBookingData={lexOfficeBookingData}
            onSave={handleSaveBookingData}
            onRestore={handleRestoreBookingData}
            onClose={() => setShowBookingData(false)}
            onImport={handleImport}
            onReject={handleReject}
            isImporting={isImporting}
            isRejecting={isRejecting}
            isImportInProgress={isImportInProgress}
            errorMessage={importJob?.errorMessage || receipt?.errorMessage}
          />
        )}
      {!isUserMessage &&
        showDatevBookingData &&
        displayDatevData &&
        receipt && (
          <DatevBookingModal
            receipt={receipt}
            bookingData={displayDatevData}
            originalBookingData={datevBookingData}
            onSave={
              receipt.status !== 'imported' ? handleSaveDatevData : undefined
            }
            onRestore={
              receipt.status !== 'imported' ? handleRestoreDatevData : undefined
            }
            onClose={() => setShowDatevBookingData(false)}
          />
        )}

      <div
        className={`flex mb-4 ${isUserMessage ? 'justify-end' : 'justify-start'}`}
      >
        <div
          className={`
          px-4 py-2 rounded-xl shadow-md text-gray-800
          ${message.isThinking ? 'w-auto' : 'w-4/5'}
          ${isUserMessage ? 'bg-blue-100' : 'bg-white'}
        `}
        >
          {/* Message Content */}
          <div className='prose prose-sm dark:prose-invert max-w-none'>
            {message.isThinking ? (
              <ThinkingDots />
            ) : (
              <ReactMarkdown components={markdownComponents}>
                {message.text}
              </ReactMarkdown>
            )}
          </div>

          {/* Attachment Indicator - shown for all messages */}
          {message.attachmentFileName && (
            <div className='mt-2 text-xs text-gray-600 italic flex items-center gap-1'>
              <PaperClipIcon className='w-3 h-3' />
              <Filename
                storageKey={message.attachmentStorageKey}
                fileName={message.attachmentFileName}
              />
            </div>
          )}

          {/* Receipt & booking data - only for assistant messages */}
          {!isUserMessage && (
            <>
              {/* Booking Generation Indicator */}
              {(isGenerating || isReceiptProcessing || isGeneratingBooking || isAwaitingGeneration) && (
                <div className='mt-3 flex items-center gap-2 text-gray-600'>
                  <LoadingSpinner size='sm' />
                  <span className='text-sm'>
                    {isGeneratingBooking
                      ? 'Scheduling booking generation...'
                      : 'Generating booking data...'}
                  </span>
                </div>
              )}

              {/* Generate Booking Data Button - shown when receipt is ready and no generation in progress */}
              {(receipt?.status === 'ready' || receipt?.status === 'generation-failed') && message.receiptId && !generationScheduled && !isGeneratingBooking && (
                <div className='mt-3'>
                  <button
                    onClick={() => {
                      if (message.receiptId) {
                        setGenerationScheduled(true);
                        generateBooking(message.receiptId).then(success => {
                          if (!success) setGenerationScheduled(false);
                        });
                      }
                    }}
                    className='text-xs text-blue-600 hover:text-blue-800 underline'
                  >
                    Generate booking data
                  </button>
                </div>
              )}

              {/* LexOffice Booking Data Link */}
              {displayLexData && (
                <div className='mt-2'>
                  <button
                    onClick={() => setShowBookingData(true)}
                    className='text-xs text-blue-600 hover:text-blue-800 underline'
                  >
                    View and import LexOffice booking data
                  </button>
                  <ReactionStatusBadge status={receipt?.status} />
                </div>
              )}

              {/* Error Message from Receipt */}
              {(receipt?.status === 'generation-failed' || receipt?.status === 'text-extraction-failed') && receipt.errorMessage && (
                <div className='mt-3 p-2 bg-red-50 border border-red-200 rounded'>
                  <p className='text-xs text-red-600 whitespace-pre-wrap'>
                    Error: {receipt.errorMessage}
                  </p>
                  {message.bookingGenerationJobId && (
                    <button
                      onClick={() => {
                        if (message.bookingGenerationJobId)
                          retryBookingJob(message.bookingGenerationJobId);
                      }}
                      disabled={isRetrying}
                      className='mt-1 text-xs text-blue-600 hover:text-blue-800 underline disabled:text-gray-400 disabled:no-underline'
                    >
                      {isRetrying ? 'Retrying...' : 'Retry booking generation'}
                    </button>
                  )}
                </div>
              )}

              {/* DATEV Booking Data Link */}
              {displayDatevData && displayDatevData.length > 0 && (
                <div className='mt-2'>
                  <button
                    onClick={() => setShowDatevBookingData(true)}
                    className='text-xs text-blue-600 hover:text-blue-800 underline'
                  >
                    View DATEV booking data ({displayDatevData.length}{' '}
                    {displayDatevData.length === 1 ? 'entry' : 'entries'})
                  </button>
                  <ReactionStatusBadge status={receipt?.status} />
                </div>
              )}

              {/* Import Progress Indicator */}
              {isImportInProgress && (
                <div className='mt-3 flex items-center gap-2 text-gray-600'>
                  <LoadingSpinner size='sm' />
                  <span className='text-sm'>Importing booking...</span>
                </div>
              )}

              {/* Import Job Error */}
              {importJob?.status === 'failed' && importJob.errorMessage && receipt?.status === 'import-failed' && (
                <div className='mt-3 p-2 bg-red-50 border border-red-200 rounded'>
                  <p className='text-xs text-red-600 whitespace-pre-wrap'>
                    Import failed: {importJob.errorMessage}
                  </p>
                </div>
              )}
            </>
          )}

          {/* Timestamp */}
          {!message.isThinking && (
            <div className='text-xs mt-1 text-gray-500 italic'>
              {formattedTime}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default MessageBubble;
